import type { RootState } from '@/store';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { InitData, midleNestApi } from '@/store/services/MidleNestApi.service';
import { MessageType } from '@/types/message.type';
import CustomError from '@/types/CustomError.model';
import { ErrorType } from '@/types/error.type';

interface SystemState {
  status: string;
  errors: ErrorType[];
  messages: MessageType[];
  modals: string[];
  init: InitData;
}

const slice = createSlice({
  name: 'system',
  initialState: {
    status: '',
    errors: [] as ErrorType[],
    messages: [] as MessageType[],
    modals: [] as string[],
    init: {} as InitData,
  },
  reducers: {
    pushMessage: (state, { payload }) => {
      state.messages.push(payload);
      state.messages = [...state.messages];
    },
    removeMessage: (state) => {
      state.messages.shift();
      state.messages = [...state.messages];
    },
    pushError: (state, { payload }) => {
      state.errors.push(new CustomError(payload).toObject());
      state.errors = [...state.errors];
    },
    clearErrors: (state) => {
      state.errors = state.errors.filter((i) => (i.hidden = true));
    },
    hideError: (state, { payload }) => {
      const error = state.errors.find((i) => i.timestamp === payload.timestamp);
      if (error) {
        error.hidden = true;
      }
      state.errors = [...state.errors];
    },
    showModal: (state, { payload }) => {
      state.modals = [...state.modals, payload];
    },
    hideModal: (state) => {
      // state.modals.pop();
      // state.modals = [...(state.modals || [])];
      // close all instead one by one
      state.modals = [];
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      midleNestApi.endpoints.homeControllerGetInit.matchFulfilled,
      (state, { payload }) => {
        state.init = payload;
      },
    );
    builder.addDefaultCase((state, { type, payload, meta, ...rest }) => {
      if (type.match('pending')) state.status = 'loading';
      else if (type.match('rejected')) state.status = 'error';
      else if (type.match('fulfilled')) state.status = '';

      // console.log({ state, payload, type, rest });

      // console.log('import.meta.env.MODE', import.meta.env.MODE, import.meta.env.DEV);

      if (import.meta.env.DEV)
        if (type.match('rejected')) {
          if (payload?.status === 'FETCH_ERROR') {
            if (state.errors[0]?.status === 503) state.errors[0]['hidden'] = false;
            else state.errors = [new CustomError(payload).toObject(), ...state.errors.slice(0, 9)];
          } else if (payload.data && meta?.arg?.endpointName === 'notificationControllerCheck')
            state.errors = [
              new CustomError({ ...payload.data, hidden: true }).toObject(),
              ...state.errors.slice(0, 9),
            ];
          else if (payload.data)
            state.errors = [new CustomError(payload.data).toObject(), ...state.errors.slice(0, 9)];
        }
    });
  },
});

const systemActions = slice.actions;
const systemReducer = slice.reducer;

const systemState = (state: RootState): SystemState => state.system;

const selectStatus = (state: RootState) => state.system.status;
const selectErrors = (state: RootState) => state.system.errors;
const selectMessages = (state: RootState) => state.system.messages;
const selectModals = createSelector(systemState, (system) => system.modals);
const selectInitData = createSelector(systemState, (system) => system.init);

export {
  systemActions,
  systemReducer,
  selectStatus,
  selectErrors,
  selectMessages,
  selectModals,
  selectInitData,
};
export default slice;
